import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatMoney } from '../shared/formatting';

type Locale = 'hr' | 'en';

type Supplier = {
    id: number;
    name: string;
    default_credit_limit_eur: number | null;
    default_days_to_pay: number;
    lead_working_days: number | null;
    logistics_threshold_note: string | null;
    is_active: boolean | 0 | 1;
    notes: string | null;
    created_at: string;
    /** Partneri (bi_partners) koji su ovaj dobavljac; PG2 i L3 nose naziv partnera (audit 40.6x N4). */
    partner_ids?: number[];
};

type Partner = { id: number; name: string; role: string | null; gold_supplier_id: number | null };

type Draft = {
    name: string;
    default_credit_limit_eur: string;
    default_days_to_pay: number;
    lead_working_days: string;
    logistics_threshold_note: string;
    is_active: boolean;
    notes: string;
};

const EMPTY: Draft = {
    name: '', default_credit_limit_eur: '', default_days_to_pay: 8, lead_working_days: '',
    logistics_threshold_note: '', is_active: true, notes: '',
};

export function AdminGoldSuppliersSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<Supplier[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [draft, setDraft] = useState<Draft>(EMPTY);
    const [showForm, setShowForm] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const r = await api<{ rows: Supplier[]; partners?: Partner[] }>('/admin/gold-suppliers', token);
            setRows(r.rows);
            setPartners(r.partners ?? []);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    async function savePartners(r: Supplier, ids: number[]) {
        setError(''); setMsg('');
        try {
            await mutate(`/admin/gold-suppliers/${r.id}`, token, 'PATCH', { partner_ids: ids });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function save() {
        setError(''); setMsg('');
        const payload = {
            name: draft.name,
            default_credit_limit_eur: draft.default_credit_limit_eur === '' ? null : parseFloat(draft.default_credit_limit_eur),
            default_days_to_pay: draft.default_days_to_pay,
            lead_working_days: draft.lead_working_days === '' ? null : parseInt(draft.lead_working_days, 10),
            logistics_threshold_note: draft.logistics_threshold_note || null,
            is_active: draft.is_active,
            notes: draft.notes || null,
        };
        try {
            if (editingId !== null) {
                await mutate(`/admin/gold-suppliers/${editingId}`, token, 'PATCH', payload);
            } else {
                await mutate('/admin/gold-suppliers', token, 'POST', payload);
            }
            setEditingId(null); setShowForm(false); setDraft(EMPTY);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    function startEdit(r: Supplier) {
        setEditingId(r.id);
        setDraft({
            name: r.name,
            default_credit_limit_eur: r.default_credit_limit_eur != null ? String(r.default_credit_limit_eur) : '',
            default_days_to_pay: r.default_days_to_pay,
            lead_working_days: r.lead_working_days != null ? String(r.lead_working_days) : '',
            logistics_threshold_note: r.logistics_threshold_note ?? '',
            is_active: !!r.is_active,
            notes: r.notes ?? '',
        });
        setShowForm(true);
    }

    function cancelEdit() {
        setEditingId(null); setShowForm(false); setDraft(EMPTY);
    }

    async function remove(r: Supplier) {
        if (!confirm(t(`Obrisati dobavljača ${r.name}?`, `Delete supplier ${r.name}?`))) return;
        try {
            await mutate(`/admin/gold-suppliers/${r.id}`, token, 'DELETE');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function seedFromMap() {
        if (!confirm(t('Učitati sve dobavljače iz najnovijeg Popis xlsx-a? Postojeći se neće duplirati.', 'Seed all suppliers from the latest Popis xlsx? Existing entries will be skipped.'))) return;
        try {
            const r = await mutate<{ inserted: number; message: string }>('/admin/gold-suppliers/seed-from-map', token, 'POST', {});
            setMsg(r.message);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🏭</div>
                <div className="bi-module-hero-text">
                    <h1>129 · {t('Dobavljači zlata', 'Gold suppliers')}</h1>
                    <p className="muted">
                        {t(
                            'Master data dobavljača sa kreditnim limitima i rokovima dospijeća. Cash Schedule (m.40.1) koristi default_days_to_pay za izračun dospijeća kad Slaven potvrdi narudžbu.',
                            'Suppliers master data with credit limits and payment terms. Cash Schedule (m.40.1) uses default_days_to_pay to set the due date when Slaven confirms an order.',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {msg ? <p style={{ color: '#1f7a36', fontSize: '0.86rem' }}>✓ {msg}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <span className="muted" style={{ fontSize: '0.84rem' }}>{rows.length} {t('dobavljača', 'suppliers')}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="secondary compact" onClick={() => void seedFromMap()}>
                        🌱 {t('Seed iz Popis xlsx-a', 'Seed from Popis xlsx')}
                    </button>
                    <button type="button" className="compact" onClick={() => { cancelEdit(); setShowForm(true); }} disabled={showForm}>
                        + {t('Novi dobavljač', 'New supplier')}
                    </button>
                </div>
            </div>

            {showForm ? (
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <h3 style={{ marginTop: 0, fontSize: '0.95rem', color: 'var(--ad-ink)' }}>
                        {editingId !== null ? t('Uredi dobavljača', 'Edit supplier') : t('Novi dobavljač', 'New supplier')}
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                        <label className="filter-label" style={{ margin: 0 }}>
                            {t('Naziv', 'Name')}
                            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="AHIT" />
                        </label>
                        <label className="filter-label" style={{ margin: 0 }}>
                            {t('Kreditni limit (EUR)', 'Credit limit (EUR)')}
                            <input type="number" min="0" step="1000" value={draft.default_credit_limit_eur} onChange={(e) => setDraft({ ...draft, default_credit_limit_eur: e.target.value })} placeholder="1000000" />
                        </label>
                        <label className="filter-label" style={{ margin: 0 }}>
                            {t('Rok dospijeća (dana)', 'Days to pay')}
                            <input type="number" min="0" max="120" value={draft.default_days_to_pay} onChange={(e) => setDraft({ ...draft, default_days_to_pay: parseInt(e.target.value || '0', 10) })} />
                        </label>
                        <label className="filter-label" style={{ margin: 0 }}>
                            {t('Lead isporuke (radnih dana)', 'Delivery lead (working days)')}
                            <input type="number" min="0" max="120" value={draft.lead_working_days} onChange={(e) => setDraft({ ...draft, lead_working_days: e.target.value })} placeholder={t('npr. 10', 'e.g. 10')} title={t('Od narudžbe do prispijeća robe — koristi se za prijedlog datuma isporuke', 'From order to goods arrival — drives delivery-date suggestion')} />
                        </label>
                    </div>
                    <label className="filter-label" style={{ marginTop: 10 }}>
                        {t('Logistička napomena', 'Logistics note')}
                        <input value={draft.logistics_threshold_note} onChange={(e) => setDraft({ ...draft, logistics_threshold_note: e.target.value })}
                            placeholder={t('Npr. Pošiljka kreće kad doseže 1.000.000 € ili 3 uzastopne narudžbe', 'e.g. Shipment goes out at 1M € accumulated or 3 consecutive orders')} />
                    </label>
                    <label className="filter-label" style={{ marginTop: 10 }}>
                        {t('Bilješka', 'Notes')}
                        <input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                    </label>
                    <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', cursor: 'pointer', marginTop: 10, fontSize: '0.86rem' }}>
                        <input type="checkbox" checked={draft.is_active} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} style={{ accentColor: 'var(--ad-acc)' }} />
                        <span>{t('Aktivan', 'Active')}</span>
                    </label>
                    <div style={{ display: 'flex', gap: 6, marginTop: 14, justifyContent: 'flex-end' }}>
                        <button type="button" className="secondary compact" onClick={cancelEdit}>{t('Odustani', 'Cancel')}</button>
                        <button type="button" className="compact" onClick={() => void save()} disabled={!draft.name}>
                            💾 {t('Spremi', 'Save')}
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="table-wrap">
                <table className="admin-user-table">
                    <thead>
                        <tr>
                            <th>{t('Naziv', 'Name')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Kreditni limit', 'Credit limit')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Rok (dana)', 'Days to pay')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Lead (r.d.)', 'Lead (w.d.)')}</th>
                            <th>{t('Logistika', 'Logistics')}</th>
                            <th title={t('Partneri iz šifrarnika partnera koji su ovaj dobavljač (kvačica po partneru)', 'Partners that are this supplier (one checkbox per partner)')}>{t('Partneri', 'Partners')}</th>
                            <th>{t('Aktivan', 'Active')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td><strong>{r.name}</strong>{r.notes ? <><br /><span className="muted" style={{ fontSize: '0.76rem' }}>{r.notes}</span></> : null}</td>
                                <td style={{ textAlign: 'right' }}>{r.default_credit_limit_eur != null ? formatMoney(r.default_credit_limit_eur, locale) : <span className="muted">—</span>}</td>
                                <td style={{ textAlign: 'right' }}>{r.default_days_to_pay}d</td>
                                <td style={{ textAlign: 'right' }}>{r.lead_working_days != null ? `${r.lead_working_days}` : <span className="muted">—</span>}</td>
                                <td className="muted" style={{ fontSize: '0.82rem', maxWidth: 280 }}>{r.logistics_threshold_note ?? '—'}</td>
                                <td>
                                    {/* Kvacica mijenja samo svog partnera; ostale veze ostaju (audit prod v355.151 S2). */}
                                    <div style={{ fontSize: '0.74rem', minWidth: 180, maxHeight: 96, overflowY: 'auto' }}>
                                        {partners.filter((p) => p.gold_supplier_id == null || p.gold_supplier_id === r.id).map((p) => {
                                            const on = (r.partner_ids ?? []).includes(p.id);
                                            return (
                                                <label key={p.id} style={{ display: 'block', whiteSpace: 'nowrap' }}>
                                                    <input type="checkbox" checked={on} onChange={() => {
                                                        const cur = r.partner_ids ?? [];
                                                        if (on && !window.confirm(t(`Odvezati ${p.name} od ${r.name}?`, `Unlink ${p.name} from ${r.name}?`))) return;
                                                        void savePartners(r, on ? cur.filter((id) => id !== p.id) : [...cur, p.id]);
                                                    }} /> {p.name}
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {(r.partner_ids ?? []).length === 0 ? <div style={{ fontSize: '0.7rem', color: '#b14040' }}>{t('Nije vezan: netiranje L3 i registra ide po nazivu', 'Not linked: netting falls back to name')}</div> : null}
                                </td>
                                <td>{r.is_active ? '✓' : '✗'}</td>
                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    <button type="button" className="secondary compact" onClick={() => startEdit(r)} style={{ marginRight: 4 }}>✎</button>
                                    <button type="button" className="secondary compact" onClick={() => void remove(r)} style={{ color: '#b14040' }}>🗑</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
